import type {
  MonthlySteppedValues,
  TMonthlyTemplateValues,
} from './templates/monthly';
import type { TFormItem } from './types';
import type {
  InitialSteppedValues,
  TInitialTemplateValues,
} from './templates/initial';

export const reduceInitialDataBySteps = (
  values: TInitialTemplateValues,
): InitialSteppedValues => {
  return {
    STEP1: {
      confirmInitiatingVisit: values.confirmInitiatingVisit,
      visitDate: values.visitDate,
      visitType: values.visitType,
    },
    STEP2: {
      recentHealthcareActivity: values.recentHealthcareActivity,
      dates: values.dates,
      details: values.details,
      providerAppointmentOtherText: values.providerAppointmentOtherText,
    },
    STEP3: {
      medicationDiscrepancies: values.medicationDiscrepancies,
      patientTakingMedicationsAsPrescribed:
        values.patientTakingMedicationsAsPrescribed,
      timing: values.timing,
      wasMedRecPerformed: values.wasMedRecPerformed,
      inAdvanceOfAppointmentOnText: values.inAdvanceOfAppointmentOnText,
      medicationDiscrepanciesYesText: values.medicationDiscrepanciesYesText,
      patientTakingMedicationsAsPrescribedNoText:
        values.patientTakingMedicationsAsPrescribedNoText,
    },
    STEP4: {
      condition1: values.condition1,
      condition1DiagnosisText: values.condition1DiagnosisText,
      condition1DiagnosisICDText: values.condition1DiagnosisICDText,
      condition1CurrentInterventionsText:
        values.condition1CurrentInterventionsText,
      condition1MeasurableGoalText: values.condition1MeasurableGoalText,
      condition2: values.condition2,
      condition2DiagnosisText: values.condition2DiagnosisText,
      condition2DiagnosisICDText: values.condition2DiagnosisICDText,
      condition2CurrentInterventionsText:
        values.condition2CurrentInterventionsText,
      condition2MeasurableGoalText: values.condition2MeasurableGoalText,

      individualizedCarePlanCreated: values.individualizedCarePlanCreated,
      careCoordinationList: values.careCoordinationList,
      carePlanCreatedYesList: values.carePlanCreatedYesList,
      educationAndSelfMonitoringList: values.educationAndSelfMonitoringList,
      interventionsList: values.interventionsList,
      medicationManagementList: values.medicationManagementList,
      goalList: values.goalList,

      barriers: values.barriers,
      barriersOtherText: values.barriersOtherText,
      additionalBarrierNotes: values.additionalBarrierNotes,

      planIncludes: values.planIncludes,
    },
    STEP5: {
      anyUnmetNeeds: values.anyUnmetNeeds,
      phq2Completed: values.phq2Completed,
      phq2CompletedNoText: values.phq2CompletedNoText,
      phq2CompletedYesText: values.phq2CompletedYesText,
      sdohAssessmentCompleted: values.sdohAssessmentCompleted,
      anyUnmetNeedsYesText: values.anyUnmetNeedsYesText,
    },
  };
};

export const reduceMonthlyDataBySteps = (
  values: TMonthlyTemplateValues,
): MonthlySteppedValues => {
  return {
    STEP1: {
      hasRecentHealthcareActivity: values.hasRecentHealthcareActivity,
      recentHealthcareActivity: values.recentHealthcareActivity,
      recentHealthcareActivityOtherText:
        values.recentHealthcareActivityOtherText,
    },
    STEP2: {
      wasMedRecPerformed: values.wasMedRecPerformed,
      timing: values.timing,
      medicationDiscrepancies: values.medicationDiscrepancies,
      medicationDiscrepanciesYesText: values.medicationDiscrepanciesYesText,
      patientAdherent: values.patientAdherent,
      adherenceBarriersIndicated: values.adherenceBarriersIndicated,
      adherenceBarriersIndicatedYesText:
        values.adherenceBarriersIndicatedYesText,
      patientAdherentNoText: values.patientAdherentNoText,
      adherenceBarriersDiscussed: values.adherenceBarriersDiscussed,
      changesSideEffectsNoted: values.changesSideEffectsNoted,
      changesSideEffectsNotedYesText: values.changesSideEffectsNotedYesText,
    },
    STEP3: {
      goals: values.goals,
      goalsOtherText: values.goalsOtherText,
      barriers: values.barriers,
      barriersOtherText: values.barriersOtherText,
      betterControlOfList: values.betterControlOfList,
      didNurseUpdateCarePlan: values.didNurseUpdateCarePlan,
      measurableTargets: values.measurableTargets,
      measurableTargetsOtherText: values.measurableTargetsOtherText,
      dateCarePlanLastUpdated: values.dateCarePlanLastUpdated,
      carePlanReviewedAtThisEncounter: values.carePlanReviewedAtThisEncounter,
    },
    STEP4: {
      adlFunctionalStatusChecked: values.adlFunctionalStatusChecked,
      behavioralHealthScreening: values.behavioralHealthScreening,
      cognitiveStatusScreened: values.cognitiveStatusScreened,
      escalationsRedFlagsToday: values.escalationsRedFlagsToday,
      escalationsRedFlagsTodayYesText: values.escalationsRedFlagsTodayYesText,
      fallSafetyRiskAssessed: values.fallSafetyRiskAssessed,
    },
    STEP5: {
      anyUnmetNeedsOrReferralsThisMonth:
        values.anyUnmetNeedsOrReferralsThisMonth,
      anyUnmetNeedsOrReferralsThisMonthYesText:
        values.anyUnmetNeedsOrReferralsThisMonthYesText,
      sdohNeedsAssessmentUpdated: values.sdohNeedsAssessmentUpdated,
    },
    STEP6: {
      outstandingItemsNextDue: values.outstandingItemsNextDue,
      preventiveCareReviewed: values.preventiveCareReviewed,
    },
    STEP7: {
      anyReferralsFollowUps: values.anyReferralsFollowUps,
      anyReferralsFollowUpsYesText: values.anyReferralsFollowUpsYesText,
      coordinationWithProviders: values.coordinationWithProviders,
    },
  };
};

export const checkStepErrors = <
  T extends Record<number, { title: string; template: TFormItem[] }>,
  E extends Record<string, string | string[]> | undefined,
>(
  steppedTemplates: T,
  errors: E,
) => {
  return Object.entries(steppedTemplates)
    .filter(([_key, { template }]) => {
      const recursiveCheck = (re: TFormItem[]) => {
        return re.some((i): boolean => {
          if (i.type === 'info') return false;

          const err = errors?.[i.name];
          if (err !== undefined) return true;

          if (i.items && recursiveCheck(i.items)) return true;

          if (i.options?.some((o) => o.subItems && recursiveCheck(o.subItems)))
            return true;

          return false;
        });
      };
      return recursiveCheck(template);
    })
    .map(([key]) => +key + 1);
};

export const checkIsErrorExist = <
  T extends Record<string, string | string[]> | undefined,
>(
  item: TFormItem,
  errors: T,
): boolean => {
  if (item.type === 'info') return false;

  const err = errors?.[item.name];
  const itemsError = item?.items?.some((i) => checkIsErrorExist(i, errors));
  const itemOptionSubItemsError = item?.options?.some((o) =>
    o?.subItems?.some((si) => checkIsErrorExist(si, errors)),
  );

  return !!err || !!itemsError || !!itemOptionSubItemsError;
};
